import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  logo?: string;
  colors: { primary: string; secondary: string };
  status: string;
  memberCount: number;
  manager: { id: string; firstName: string; lastName: string } | null;
}

const TeamApprovals: React.FC = () => {
  const { user } = useAuth();
  const [pendingTeams, setPendingTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdminOrSuperAdmin(user?.profileType)) {
      fetchPendingTeams();
    }
  }, [user]);

  const fetchPendingTeams = async () => {
    try {
      const response = await api.get('/teams/pending/approval');
      setPendingTeams(response.data);
    } catch (error) {
      console.error('Error fetching pending teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (teamId: string, status: 'approved' | 'rejected') => {
    try {
      await api.patch(`/teams/${teamId}/approval`, { status });
      fetchPendingTeams();
      alert(`Team ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update team status');
    }
  };

  if (!isAdminOrSuperAdmin(user?.profileType)) {
    return <div className="container">Access denied. Admin only.</div>;
  }

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1>Team Approvals</h1>
      <p style={{ color: '#757575', marginBottom: '20px' }}>
        Review and approve or reject team creation requests
      </p>

      {pendingTeams.length === 0 ? (
        <div className="card">
          <p>No pending team approvals.</p>
        </div>
      ) : (
        <div className="grid">
          {pendingTeams.map((team) => (
            <div key={team.id} className="team-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                {team.logo ? (
                  <img src={team.logo} alt={team.name} style={{ width: '50px', height: '50px', borderRadius: '12px' }} />
                ) : (
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: team.colors.primary,
                      border: `3px solid ${team.colors.secondary}`
                    }}
                  />
                )}
                <div>
                  <h3>{team.name}</h3>
                  <p style={{ color: '#757575', fontSize: '14px' }}>{team.teamNumber}</p>
                </div>
              </div>
              <p><strong>Manager:</strong> {team.manager ? `${team.manager.firstName} ${team.manager.lastName}` : 'N/A'}</p>
              <p><strong>Members:</strong> {team.memberCount}</p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => handleApproval(team.id, 'approved')}
                >
                  Approve
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleApproval(team.id, 'rejected')}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamApprovals;

